// src/utils/responseTime.ts
// -----------------------------------------------------------------------------
// "Response Time" metric for the Reports page: how long (in business hours) a
// responder task takes from being received to being actioned.
//
// Rules (agreed 2026-07-01):
//   • Only tasks RECEIVED (created) between 08:00 and 16:45 LOCAL time count.
//   • Elapsed time is measured in BUSINESS HOURS only — the 08:00–18:00 window
//     each day. Time from 18:00–08:00 (overnight) is NOT counted. Weekends are
//     counted (no weekday rule was specified).
//   • Times are evaluated in the viewer's LOCAL (office) timezone.
//
// Pure + framework-free so it can be unit-tested without the DB or React.
// -----------------------------------------------------------------------------

const DAY_START_HOUR = 8; // 08:00 — business day start
const DAY_END_HOUR = 18; // 18:00 — business day end (6pm)
const RECEIVE_FROM_MIN = 8 * 60; // 08:00
const RECEIVE_TO_MIN = 16 * 60 + 45; // 16:45

// True when `iso`'s LOCAL time-of-day falls within the receive window
// 08:00–16:45 (inclusive). Invalid/missing → false.
export function receivedInWindow(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const mins = d.getHours() * 60 + d.getMinutes();
  return mins >= RECEIVE_FROM_MIN && mins <= RECEIVE_TO_MIN;
}

// Business hours (08:00–18:00 local, every day) elapsed between two timestamps.
// Overnight time (18:00–08:00) is excluded. Returns hours (may be fractional);
// 0 when the range is empty/invalid.
export function businessHoursBetween(
  startIso: string | null | undefined,
  endIso: string | null | undefined
): number {
  if (!startIso || !endIso) return 0;
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return 0;

  let totalMs = 0;
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0); // walk day-by-day from the start date's midnight
  while (cursor.getTime() <= end.getTime()) {
    const dayStart = new Date(cursor);
    dayStart.setHours(DAY_START_HOUR, 0, 0, 0);
    const dayEnd = new Date(cursor);
    dayEnd.setHours(DAY_END_HOUR, 0, 0, 0);
    const winStart = Math.max(start.getTime(), dayStart.getTime());
    const winEnd = Math.min(end.getTime(), dayEnd.getTime());
    if (winEnd > winStart) totalMs += winEnd - winStart;
    cursor.setDate(cursor.getDate() + 1);
  }
  return totalMs / 3_600_000;
}

// Average business-hours response time over the given tasks — counting only
// tasks that are actioned (have completed_at) AND were received in the
// 08:00–16:45 window. Returns null when no task qualifies.
export function averageResponseHours(
  tasks: Array<{ created_at?: string | null; completed_at?: string | null }>
): number | null {
  const values: number[] = [];
  for (const t of tasks) {
    if (!t.created_at || !t.completed_at) continue;
    if (!receivedInWindow(t.created_at)) continue;
    values.push(businessHoursBetween(t.created_at, t.completed_at));
  }
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}
